import { useEffect } from "react";
import { useRouter } from "next/router";
import NextLink from "next/link";
import { useTranslation } from "next-i18next";
import { Box, Container, Grid } from "@mui/material";
import BreadcrumbsComponent from "@atoms/Breadcrumbs/Breadcrumbs";
import ButtonHelp from "@atoms/ButtonHelp";
import { Section } from "@atoms/Section";
import Button from "@ds/components/Button";
import DynamicImage from "@ds/components/DynamicImage";
import Text from "@ds/components/Text";
import color from "@definitions/mui/color";
import { isEmpty, isEmptyObject } from "@helpers/common";
import Footer from "@layouts/Footer";
import ContainerUVP from "../../container/UVP";
import ContainerVariants from "../../container/Variants";
import ContainerInterest from "../../container/Interest";
import ContainerSuccessStory from "../../container/SuccessStory";
import ContainerBenefitFeature from "../../container/BenefitFeature";
import Header from "@layouts/Main/lib/Header";
import YoutubePlayer from "@fragments/YoutubePlayer/youtubePlayer";
import { gtmCustomEvent } from "@helpers/analytics";
import useResponsive from "@helpers/useResponsive";
import { isAllowInterest } from "@config/feature";

const ProductLevelOne: React.FC<any> = ({ data, dataNavbar }) => {
  const {
    breadcrumbs,
    productVariant,
    productDocument,
    productYoutubeId,
    productName,
    productImage,
    level,
    story,
    uvp,
    successStory,
    testimoni,
    keyFeatureAndBenefit,
    title,
  } = data;
  const router = useRouter();
  const levelZero = `${router.query?.level_zero}`;
  const levelOne = `${router.query?.level_one}`;
  const { t } = useTranslation("common");
  const isTablet = useResponsive("lg");
  const tabletPadding = isTablet ? "110px" : "auto";
  const hasVariantProduct = productVariant?.length > 0;
  const hasStoryAndTestimoni = level >= 1 && (successStory.length > 0 || testimoni.length > 0);
  const hasKeyFeature = !isEmptyObject(keyFeatureAndBenefit?.keyFeature || {});
  const hasBenefitFeature = !isEmptyObject(keyFeatureAndBenefit?.benefit || {});

  useEffect(() => {
    const timeoutFetch = setTimeout(() => {
      gtmCustomEvent(
        {
          eventAction: `Akses PDP - ${productName}`,
          eventCategory: "WebPages",
          eventLabel: `PDP - ${productName}`,
        },
        "LeadProduct",
      );
    }, 1000);
    return () => window.clearTimeout(timeoutFetch);
  }, [productName]);

  return (
    <>
      <Header
        setExpand={function (): void {
          throw new Error("Function not implemented.");
        }}
        withoutNavigation
        css={{ pointerEvents: "none" }}
      />
      <Box position="relative" mt="70px" minHeight="100vh">
        <Box bgcolor={color.grey.mid} px="16px" py={1} width="100%">
          <Container maxWidth="lg" disableGutters>
            <BreadcrumbsComponent
              homeUrl={{ label: t("root"), productPath: "/" }}
              breadcrumbs={breadcrumbs}
            />
          </Container>
        </Box>
        <Section bgcolor="white" maxWidth="full-width" px={tabletPadding}>
          <Container maxWidth="lg" disableGutters>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item xs={7}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  mr={4}
                >
                  <Text variant="h1" weight="700">
                    {title}
                  </Text>
                  <Box mt={2}>
                    <Text color="inactive-secondary" variant="body1" weight="600">
                      {story}
                    </Text>
                  </Box>
                  <Box display="flex" gap={2} mt={4}>
                    {isAllowInterest && (
                      <NextLink href={`/${levelZero}/${levelOne}/interest-form`} passHref>
                        <Button size="large">{t("interest.button")}</Button>
                      </NextLink>
                    )}
                    {!isEmpty(productDocument) && (
                      <NextLink href={productDocument?.mediaPath} passHref>
                        <a target="_blank">
                          <Button size="large" variant="secondary">
                            {t("interest.learn")}
                          </Button>
                        </a>
                      </NextLink>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box display="flex" justifyContent="flex-end" alignItems="flex-start">
                  <Box maxWidth={427}>
                    {productYoutubeId ? (
                      <YoutubePlayer image={productImage} productYoutubeId={productYoutubeId} />
                    ) : (
                      <DynamicImage
                        src={productImage?.mediaPath}
                        alt={productImage?.mediaName}
                        borderRadius={6}
                        priority
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Section>
        {uvp && (
          <Section bgcolor={color.grey.mid} maxWidth="full-width" px={tabletPadding} pt={4} pb={5}>
            <Container maxWidth="lg" disableGutters>
              <ContainerUVP uvp={uvp} />
            </Container>
          </Section>
        )}
        {hasKeyFeature && (
          <Section bgcolor="white" maxWidth="full-width" px={tabletPadding}>
            <Container maxWidth="lg" disableGutters>
              <ContainerBenefitFeature
                data={keyFeatureAndBenefit.keyFeature}
                isRowReverse={false}
              />
            </Container>
          </Section>
        )}
        {hasBenefitFeature && (
          <Section bgcolor="white" maxWidth="full-width" px={tabletPadding}>
            <Container maxWidth="lg" disableGutters>
              <ContainerBenefitFeature data={keyFeatureAndBenefit.benefit} isRowReverse={true} />
            </Container>
          </Section>
        )}
        {hasVariantProduct && (
          <ContainerVariants dataVariant={productVariant} productName={title} />
        )}
        {hasStoryAndTestimoni && (
          <Section title={title + " " + t("pdp.testimoni")} description="" maxWidth="full-width" px={tabletPadding}>
            <Container maxWidth="lg" disableGutters>
              <ContainerSuccessStory article={successStory || []} testimoni={testimoni || []} />
            </Container>
          </Section>
        )}
        <Section id="interestSection" variant="gray" maxWidth="full-width" px={tabletPadding}>
          <Container maxWidth="lg" disableGutters>
            <ContainerInterest
              pathLevel={`/${levelZero}/${levelOne}`}
              productImage={productImage}
              productName={title}
            />
          </Container>
        </Section>
        <ButtonHelp />
        <Footer bgColor="white" dataNavbar={dataNavbar} />
      </Box>
    </>
  );
};

export default ProductLevelOne;
